import React from "react";
import { Form, Formik, FormikProps } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { ReminderData, defaultReminderData, reminderSchema } from "../../core/api/reminder/ReminderModel";
import { useNotifications } from "../../hooks/NotificationProvider";
import EntityId from "../../core/api/EntityId";
import { translations } from "../../translations/common";


const FormTextField = styled(Grid)({
  "& > *": {
    width: "100%",
  },
});

interface Props {
  value: ReminderData;
  save(model: ReminderData): Promise<EntityId>;
  close(): void;
}

const ReminderModalForm: React.FC<Props> = ({ save, close, value }) => {
  const notifications = useNotifications();

  // Format date for datetime-local input
  const formatDateForInput = (date: string): string => {
    return date.slice(0, 16);
  };

  return (
    <Dialog
      open={true}
      onClose={close}
      aria-labelledby="form-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="form-dialog-title">{translations.reminders.form.title}</DialogTitle>
      <Formik
        initialValues={value || defaultReminderData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          actions.setSubmitting(true);
          save(values)
            .then(close)
            .then(() =>
              notifications.notify({
                level: "success",
                message: translations.reminders.form.notifications.success.message,
              })
            )
            .catch((error) => {
              notifications.notify({
                level: "error",
                message: error.data || translations.reminders.form.notifications.error.message,
              });
            })
            .finally(() => {
              actions.setSubmitting(false);
            });
        }}
        validationSchema={reminderSchema}
      >
        {(props: FormikProps<ReminderData>) => {
          const {
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            isSubmitting,
          } = props;
          return (
            <Form>
              <DialogContent>
                <Grid
                  container
                  justifyContent="space-around"
                  direction="row"
                  spacing={2}
                >
                  <FormTextField size={12}>
                    <TextField
                      id="date"
                      type="datetime-local"
                      name="date"
                      label={translations.reminders.form.fields.date}
                      onChange={handleChange}
                      helperText={touched.date && (errors.date as string)}
                      error={touched.date && Boolean(errors.date)}
                      fullWidth
                      value={formatDateForInput(values.date)}
                      onBlur={handleBlur}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </FormTextField>
                  <FormTextField size={12}>
                    <TextField
                      name="comment"
                      id="comment"
                      label={translations.reminders.form.fields.comment}
                      value={values.comment}
                      type="text"
                      multiline
                      rows={4}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.comment && Boolean(errors.comment)}
                      helperText={touched.comment && (errors.comment as string)}
                    />
                  </FormTextField>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  type="button"
                  className="outline"
                  onClick={close}
                  disabled={isSubmitting}
                >
                  {translations.reminders.form.buttons.cancel}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="contained"
                  color="primary"
                >
                  {translations.reminders.form.buttons.save}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default ReminderModalForm;
